import { centavos, diasEntre, fromUnits, lineTotal, newId, type Centavos, type Milli, type PayloadInputOf, type UnitCost } from '@abastece/shared';
import { precioId, type Cliente, type FormaPagoVenta, type Pedido, type Producto, type UnidadVenta, type Venta } from '@abastece/core';
import { enRango } from './costos.js';
import { comoNombre } from './texto.js';

/**
 * Los pedidos: the order being taken, rung up the way the till rings up a sale (the owner,
 * 2026-10-04), and the list of them, which is where an order is charged from when its day comes.
 * Pure, so the tests can walk it: `ui/TomarPedido.tsx` and `routes/Pedidos.tsx` draw it.
 */

const UNO = fromUnits(1);

/** A line of the order being taken: one product in one presentation, at the price agreed. */
export interface LineaPedido {
  key: string;
  producto: Producto;
  unidad: UnidadVenta;
  cantidad: Milli;
  /**
   * The list the line's price came from, when it is not the order's (the owner, 2026-10-18: «set the
   * retail/wholesale price toggle on each item like in the sale screen»), picked at the foot of its
   * card as at the till. Absent, the order's.
   */
  listaId?: string;
  /** What its list asks for it, to tell a price agreed by hand from the list's own. */
  precioLista: Centavos;
  /** The price agreed: the list's, unless somebody typed another. Zero while nobody has priced it. */
  precioUnitario: Centavos;
  /** Why the price agreed is not the list's, asked as the till asks it (the owner, 2026-10-05). */
  motivoOverride: string | null;
  /** The owner who allowed a price below the margin floor, or one brought down with no cost known. */
  autorizadoPor: string | null;
}

/**
 * A product onto the order, as the till rings one up: the same product in the same presentation
 * again is one more of it, not a second line, whether it was scanned again or picked again. A line
 * of something the list has no price for comes in at nothing, to be priced (`precio` null).
 */
export function agregarAlPedido(
  lineas: readonly LineaPedido[],
  nueva: { key: string; producto: Producto; unidad: UnidadVenta; precio: Centavos | null },
): LineaPedido[] {
  const existente = lineas.find((l) => l.producto.id === nueva.producto.id && l.unidad.id === nueva.unidad.id);
  if (existente) return lineas.map((l) => (l === existente ? { ...l, cantidad: (l.cantidad + UNO) as Milli } : l));
  const precio = nueva.precio ?? (0 as Centavos);
  return [
    ...lineas,
    {
      key: nueva.key,
      producto: nueva.producto,
      unidad: nueva.unidad,
      cantidad: UNO,
      precioLista: precio,
      precioUnitario: precio,
      motivoOverride: null,
      autorizadoPor: null,
    },
  ];
}

/**
 * The line as the till's editor left it (`ui/EditorLinea.tsx`): its count, its presentation and
 * that presentation's list price, the price, and why it is not the list's.
 *
 * Who allowed the price is what this save says (`autorizadoPor`): the owner whose PIN was typed, or
 * the owner taking the order, for a price below the margin floor or brought down with no cost
 * known; nobody for any other. A yes is for the price it was given to, so a line put back at the
 * list's price, or at the case's when the case is picked, does not carry one on to the sale.
 */
export function conCambios(
  l: LineaPedido,
  cambios: {
    cantidad?: Milli;
    precioUnitario?: Centavos;
    unidad?: UnidadVenta | null;
    precioLista?: Centavos;
    motivoOverride?: string | null;
  },
  autorizadoPor: string | null,
): LineaPedido {
  return {
    ...l,
    cantidad: cambios.cantidad ?? l.cantidad,
    precioUnitario: cambios.precioUnitario ?? l.precioUnitario,
    unidad: cambios.unidad ?? l.unidad,
    precioLista: cambios.precioLista ?? l.precioLista,
    motivoOverride: cambios.motivoOverride === undefined ? l.motivoOverride : cambios.motivoOverride || null,
    autorizadoPor,
  };
}

/**
 * The order at another list, its client's: every line at its price there, as the till moves a sale
 * when a client is picked, one at a list of its own too. A price somebody agreed by hand stays where
 * they put it, and so does a line the new list has no price for, rather than dropping to nothing:
 * each keeps the list its price came from, the order's until now (`antes`) when it had none of its own.
 */
export function conLista(
  lineas: readonly LineaPedido[],
  precioDe: (unidadVentaId: string) => Centavos | null,
  antes?: string,
): LineaPedido[] {
  return lineas.map((l) => {
    const precio = l.precioUnitario === l.precioLista ? precioDe(l.unidad.id) : null;
    if (precio === null) return l.listaId || !antes ? l : { ...l, listaId: antes };
    const { listaId: _, ...suya } = l;
    return { ...suya, precioLista: precio, precioUnitario: precio };
  });
}

/**
 * One line at another list, from the row at the foot of its card, as at the till (the owner,
 * 2026-10-18): the case of oil at wholesale, the rest of the order at the client's list. It takes the
 * list's price, one typed by hand included, and the reason for that one goes, as does the owner's yes
 * to it: a list's own price needs neither. At the order's own list it has none of its own. A list with
 * no price for it moves nothing.
 */
export function aLaLista(l: LineaPedido, listaId: string, precio: Centavos | null, listaDelPedido: string): LineaPedido {
  if (precio === null) return l;
  const { listaId: _, ...suya } = l;
  return {
    ...suya,
    ...(listaId === listaDelPedido ? {} : { listaId }),
    precioLista: precio,
    precioUnitario: precio,
    motivoOverride: null,
    autorizadoPor: null,
  };
}

/**
 * One line in another of its product's presentations, from the row at the foot of its card, as at
 * the till (the owner, 2026-10-18): the box of 12 Oreos as one box, not as 12 packets. As many as it
 * had, of the new one, at its price on the line's list; a price typed by hand goes with its reason
 * and the owner's yes to it, as `aLaLista` has it. One with no price there, or another product's,
 * moves nothing.
 */
export function aLaPresentacion(l: LineaPedido, unidad: UnidadVenta, precio: Centavos | null): LineaPedido {
  if (precio === null || unidad.productoId !== l.producto.id) return l;
  return { ...l, unidad, precioLista: precio, precioUnitario: precio, motivoOverride: null, autorizadoPor: null };
}

/** What a line comes to; null past what the books take, as a quantity typed absurdly large is. */
export const subtotalDe = (l: Pick<LineaPedido, 'precioUnitario' | 'cantidad'>): Centavos | null =>
  enRango(() => lineTotal(l.precioUnitario, l.cantidad));

/** What the order comes to; null while a line is past what the books take. */
export function totalDelPedido(lineas: readonly LineaPedido[]): Centavos | null {
  let total = 0;
  for (const l of lineas) {
    const sub = subtotalDe(l);
    if (sub === null) return null;
    total += sub;
  }
  return enRango(() => centavos(total));
}

/**
 * What the customer pays for an order: its lines, and the delivery when it has one (2026-10-12). The
 * order's own `total` is the lines alone; this is what the list and the order's receipt say.
 */
export const totalConEnvio = (p: Pick<Pedido, 'total' | 'envio'>): Centavos => centavos(p.total + (p.envio ?? 0));

/** Lines nobody has priced yet: the order is not saved with one at nothing. */
export const sinPrecio = (lineas: readonly LineaPedido[]): number => lineas.filter((l) => l.precioUnitario <= 0).length;

/**
 * `pedido.tomado`'s lines and total, from the lines on screen; null while one cannot be saved. A
 * line's reason and the owner who allowed its price go with it only when it has them, as on a sale,
 * and its list only when it is not the order's (`listaDelPedido`).
 */
export function lineasDelEvento(
  lineas: readonly LineaPedido[],
  listaDelPedido?: string,
): {
  lineas: {
    productoId: string;
    unidadVentaId: string;
    cantidad: Milli;
    precioUnitario: Centavos;
    subtotal: Centavos;
    listaId?: string;
    motivoOverride?: string;
    autorizadoPor?: string;
  }[];
  total: Centavos;
} | null {
  const total = totalDelPedido(lineas);
  if (lineas.length === 0 || total === null || sinPrecio(lineas) > 0 || lineas.some((l) => l.cantidad <= 0)) return null;
  return {
    lineas: lineas.map((l) => ({
      productoId: l.producto.id,
      unidadVentaId: l.unidad.id,
      cantidad: l.cantidad,
      precioUnitario: l.precioUnitario,
      subtotal: subtotalDe(l)!,
      ...(l.listaId && l.listaId !== listaDelPedido ? { listaId: l.listaId } : {}),
      ...(l.motivoOverride ? { motivoOverride: l.motivoOverride } : {}),
      ...(l.autorizadoPor ? { autorizadoPor: l.autorizadoPor } : {}),
    })),
    total,
  };
}

// ---------------------------------------------------------------------------
// Un pedido cambiado
// ---------------------------------------------------------------------------

/** An order as it stands, as `pedido.actualizado` says one. */
export type CambioDePedido = PayloadInputOf<'pedido.actualizado'>;

/**
 * An order's lines as the screen that takes one keeps them, to change it (the owner, 2026-10-15):
 * each at the price agreed, at its own list when it has one, with why and who allowed it. A price as
 * the list had it then is the list's still, so moving the order to another client's list moves it,
 * as on an order being taken; one agreed by hand keeps it, with its list's price as it is now beside
 * it. Null when a line's product or presentation is not on this phone: the order cannot be shown as
 * it is.
 */
export function lineasParaCambiar(
  p: Pick<Pedido, 'lineas'>,
  datos: {
    productos: readonly Producto[];
    unidades: readonly UnidadVenta[];
    /** What a list asks for a presentation now: the line's own, or the order's when it has none (`undefined`). */
    precioDe: (unidadVentaId: string, listaId?: string) => Centavos | null;
  },
): LineaPedido[] | null {
  const lineas: LineaPedido[] = [];
  for (const l of p.lineas) {
    const producto = datos.productos.find((x) => x.id === l.productoId);
    const unidad = datos.unidades.find((u) => u.id === l.unidadVentaId);
    if (!producto || !unidad) return null;
    lineas.push({
      key: newId(),
      producto,
      unidad,
      cantidad: l.cantidad,
      ...(l.listaId ? { listaId: l.listaId } : {}),
      precioLista: l.motivoOverride ? (datos.precioDe(l.unidadVentaId, l.listaId) ?? l.precioUnitario) : l.precioUnitario,
      precioUnitario: l.precioUnitario,
      motivoOverride: l.motivoOverride ?? null,
      autorizadoPor: l.autorizadoPor ?? null,
    });
  }
  return lineas;
}

/** An order as the books keep it, said as a change would say it: what a change is held against, and what puts it back. */
export function comoEsta(p: Pedido): CambioDePedido {
  return {
    pedidoId: p.id,
    clienteId: p.clienteId,
    fechaEntrega: p.fechaEntrega,
    listaId: p.listaId,
    lineas: p.lineas.map((l) => ({
      productoId: l.productoId,
      unidadVentaId: l.unidadVentaId,
      cantidad: l.cantidad,
      precioUnitario: l.precioUnitario,
      subtotal: l.subtotal,
      ...(l.listaId ? { listaId: l.listaId } : {}),
      ...(l.motivoOverride ? { motivoOverride: l.motivoOverride } : {}),
      ...(l.autorizadoPor ? { autorizadoPor: l.autorizadoPor } : {}),
    })),
    total: p.total,
    envio: p.envio ?? 0,
    nota: p.nota || null,
  };
}

/** The same thing whatever order its keys were written in. */
const canonico = (v: unknown): unknown =>
  Array.isArray(v)
    ? v.map(canonico)
    : v && typeof v === 'object'
      ? Object.fromEntries(
          Object.keys(v)
            .sort()
            .map((k) => [k, canonico((v as Record<string, unknown>)[k])]),
        )
      : v;

/** Whether two say the same order: a change that changes nothing is not saved. */
export const mismoPedido = (a: CambioDePedido, b: CambioDePedido): boolean => JSON.stringify(canonico(a)) === JSON.stringify(canonico(b));

/** A line of the till's sale, as `routes/Pos.tsx` keeps one: what an order's line becomes there. */
export interface LineaDeCaja {
  key: string;
  productoId: string;
  descripcion: string;
  unidad: UnidadVenta | null;
  cantidad: Milli;
  /** The line's own list, from the order (2026-10-18): the till rings it up there, as one picked on its card. */
  listaId?: string;
  precioLista: Centavos;
  precioUnitario: Centavos;
  motivoOverride: string | null;
  autorizadoPor: string | null;
  /**
   * The owner's yes came with the order, given for its price when it was taken. It is not a yes to
   * selling expired medicine at the door days later: the till asks for that one again.
   */
  autorizadoEnElPedido?: true;
  costoReferencia: UnitCost;
}

/**
 * An order's lines as the till rings them up, at the prices the customer was quoted when it was
 * taken, each at its own list when it has one (2026-10-18). The same for the order picked under
 * «Pedidos por entregar» and for one «Cobrar» sent from the list of orders (`?pedido=`), whose
 * screen's lists may not have filled yet: each passes what it has read.
 *
 * Each line brings why its price is not the list's and the owner who allowed it, so the sale
 * records them as if they had been typed at the till (the owner, 2026-10-05). An order taken before
 * brings neither, and charges as it always did.
 */
export function lineasDelPedido(
  p: Pedido,
  datos: {
    productos: readonly Producto[];
    unidades: readonly UnidadVenta[];
    precios: readonly { id: string; precio: Centavos }[];
    costoDe: (productoId: string) => UnitCost;
  },
  sinNombre: string,
): LineaDeCaja[] {
  return p.lineas.map((l) => {
    const producto = datos.productos.find((x) => x.id === l.productoId);
    const unidad = datos.unidades.find((u) => u.id === l.unidadVentaId) ?? null;
    const precioLista = datos.precios.find((x) => x.id === precioId(l.unidadVentaId, l.listaId ?? p.listaId))?.precio;
    return {
      key: newId(),
      productoId: l.productoId,
      descripcion: producto?.nombre ?? sinNombre,
      unidad,
      cantidad: l.cantidad,
      ...(l.listaId ? { listaId: l.listaId } : {}),
      precioLista: precioLista ?? l.precioUnitario,
      precioUnitario: l.precioUnitario,
      motivoOverride: l.motivoOverride ?? null,
      autorizadoPor: l.autorizadoPor ?? null,
      ...(l.autorizadoPor ? { autorizadoEnElPedido: true as const } : {}),
      costoReferencia: datos.costoDe(l.productoId),
    };
  });
}

/**
 * An active client already called what is typed, whatever its capitals or spacing: a new one by the
 * same name would split their fiado and their orders in two, so the sheet offers that one instead.
 */
export function clienteLlamado(clientes: readonly Cliente[], nombre: string): Cliente | undefined {
  if (!nombre.trim()) return undefined;
  return clientes.find((c) => c.activo && comoNombre(c.nombre) === comoNombre(nombre));
}

// ---------------------------------------------------------------------------
// La lista
// ---------------------------------------------------------------------------

/** How an order's day reads on the list: gone by undelivered, today's, or neither. */
export type Marca = 'atrasado' | 'hoy' | null;

export function marcaDeEntrega(fechaEntrega: string, hoy: string): Marca {
  if (fechaEntrega < hoy) return 'atrasado';
  return fechaEntrega === hoy ? 'hoy' : null;
}

/** The orders still to deliver, the soonest due first: the late ones, today's, then the rest. */
export function pendientesEnOrden(pedidos: readonly Pedido[]): Pedido[] {
  return pedidos
    .filter((p) => p.estado === 'pendiente')
    .sort((a, b) => a.fechaEntrega.localeCompare(b.fechaEntrega) || a.fecha.localeCompare(b.fecha) || a.id.localeCompare(b.id));
}

/** Delivered, the last charged first, by the sale's ticket; one whose sale is not here yet, first. */
export function entregadosEnOrden(pedidos: readonly Pedido[], ventas: ReadonlyMap<string, Venta>): Pedido[] {
  const clave = (p: Pedido) => {
    const v = p.ventaId ? ventas.get(p.ventaId) : undefined;
    return v ? `${v.fecha} ${v.ticket}` : '~';
  };
  return pedidos.filter((p) => p.estado === 'entregado').sort((a, b) => clave(b).localeCompare(clave(a)));
}

/** Cancelled, the last taken first. */
export function canceladosEnOrden(pedidos: readonly Pedido[]): Pedido[] {
  return pedidos
    .filter((p) => p.estado === 'cancelado')
    .sort((a, b) => b.fecha.localeCompare(a.fecha) || b.fechaEntrega.localeCompare(a.fechaEntrega));
}

/**
 * How a delivered order was paid: each way once, in the order the sale took them. A transfer that
 * never arrived is not one of them; one that went on the client's account is fiado now, and says so.
 */
export function formasDeLaVenta(v: Pick<Venta, 'pagos'>): FormaPagoVenta[] {
  const formas: FormaPagoVenta[] = [];
  for (const p of v.pagos) if (p.estado !== 'rechazado' && !formas.includes(p.forma)) formas.push(p.forma);
  return formas;
}

// ---------------------------------------------------------------------------
// Un entregado, abierto
// ---------------------------------------------------------------------------

/**
 * Whether a delivered order was charged as it was taken: the same products in the same
 * presentations, counts and prices, in any order. The till can change an order before charging it
 * (one more, one less, something added, a price agreed at the door), and then the order's sheet
 * says so and shows what was charged under what was ordered.
 */
export function cobradoComoSePidio(
  p: Pick<Pedido, 'lineas'>,
  v: Pick<Venta, 'lineas'>,
): boolean {
  const clave = (l: { productoId: string | null; unidadVentaId: string | null; cantidad: Milli; precioUnitario: Centavos }) =>
    [l.productoId, l.unidadVentaId, l.cantidad, l.precioUnitario].join('|');
  const pedidas = p.lineas.map(clave).sort();
  const cobradas = v.lineas.map(clave).sort();
  return pedidas.length === cobradas.length && pedidas.every((x, i) => x === cobradas[i]);
}

/** How many days after the day agreed an order was charged: none on that day or before it. */
export const diasDeAtraso = (
  p: Pick<Pedido, 'fechaEntrega'>,
  v: Pick<Venta, 'fecha'>,
): number => Math.max(0, diasEntre(p.fechaEntrega, v.fecha));
